import i18n from "../../i18n";
import React, { useEffect, useMemo } from "react";
import {
  Stack,
  Text,
  Select,
  TextInput,
  NumberInput,
  Box,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import ArrayTable from "../arrayTable";
import {
  ORDER_SERVICE_MAPPABLE_FIELDS,
  getMappableFieldByPath,
} from "../../data/orderServiceMappableFields";
import { DATA_ORDER_STATUS_OPTIONS } from "../../data/options";
import { slugifyServiceFieldKey } from "../../data/orderServiceFieldTypes";

const VALUE_SOURCE_OPTIONS = [
  { value: "fixed", label: "Fixed value" },
  { value: "serviceField", label: "Service field value" },
];

const rowInitValue = () => ({
  orderFieldPath: null,
  valueSource: "fixed",
  value: "",
  serviceFieldKey: null,
  sortOrder: 0,
});

const ALL_UPDATE_FIELD_OPTIONS = ORDER_SERVICE_MAPPABLE_FIELDS.map((field) => ({
  value: field.path,
  label: `${field.group} — ${field.label}`,
}));

const OrderFieldPathSelect = ({ value, onChange, ...props }) => (
  <Select
    {...props}
    value={value || null}
    onChange={(next) => onChange(next ?? "")}
  />
);

const ValueSourceSelect = ({ value, onChange, row, disabled, ...props }) => (
  <Select
    {...props}
    size="xs"
    disabled={disabled}
    data={VALUE_SOURCE_OPTIONS}
    value={value || "fixed"}
    onChange={(next) => {
      const source = next ?? "fixed";
      if (row) {
        if (source === "fixed") {
          row.serviceFieldKey = "";
        } else {
          row.value = "";
        }
      }
      onChange(source);
    }}
  />
);

const FixedValueInput = ({ row, value, onChange, disabled }) => {
  const mapped = getMappableFieldByPath(row?.orderFieldPath);

  if (mapped?.valueInput === "orderStatus") {
    return (
      <Select
        size="xs"
        searchable
        clearable
        disabled={disabled}
        placeholder="Select status"
        data={DATA_ORDER_STATUS_OPTIONS}
        value={value || null}
        onChange={(next) => onChange(next ?? "")}
      />
    );
  }

  if (mapped?.compatibleFieldTypes?.includes("INTEGER")) {
    return (
      <NumberInput
        size="xs"
        disabled={disabled}
        value={value ?? ""}
        onChange={(next) => onChange(next)}
      />
    );
  }

  if (mapped?.compatibleFieldTypes?.includes("DATE")) {
    return (
      <DatePicker
        size="xs"
        disabled={disabled}
        allowFreeInput
        value={value ? new Date(value) : null}
        onChange={(next) => onChange(next)}
      />
    );
  }

  return (
    <TextInput
      size="xs"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const UpdateValueCell = ({
  value,
  onChange,
  row,
  disabled,
  serviceFieldOptions,
}) => {
  const valueSource = row?.valueSource === "serviceField" ? "serviceField" : "fixed";

  if (valueSource === "serviceField") {
    return (
      <Select
        size="xs"
        searchable
        clearable
        disabled={disabled}
        placeholder="Select service field"
        data={serviceFieldOptions}
        value={row?.serviceFieldKey || null}
        onChange={(next) => {
          const key = next ?? "";
          if (row) row.serviceFieldKey = key;
          onChange(key);
        }}
      />
    );
  }

  return (
    <FixedValueInput
      row={row}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

const ServiceTypeCompleteUpdatesEditor = ({
  form,
  name = "completeOrderUpdates",
  disabled,
}) => {
  const updates = form?.values?.[name] || [];
  const serviceFields = form?.values?.fields || [];

  useEffect(() => {
    if (!form || Array.isArray(form.values?.[name])) return;
    form.setFieldValue(name, []);
  }, [form, name]);

  const serviceFieldOptions = useMemo(
    () =>
      (serviceFields || []).map((field) => ({
        value: field.key || slugifyServiceFieldKey(field.label),
        label: field.cname
          ? `${field.label || field.key} / ${field.cname}`
          : field.label || field.key,
      })),
    [serviceFields]
  );

  const tableFields = useMemo(
    () => [
      {
        header: "Order Field",
        headerWidth: "28%",
        fieldName: "orderFieldPath",
        component: OrderFieldPathSelect,
        props: {
          size: "xs",
          searchable: true,
          clearable: true,
          placeholder: "Select order field",
          data: ALL_UPDATE_FIELD_OPTIONS,
        },
      },
      {
        header: "Value Source",
        headerWidth: "22%",
        fieldName: "valueSource",
        component: ValueSourceSelect,
        props: { disabled },
      },
      {
        header: "Value",
        headerWidth: "30%",
        fieldName: "value",
        component: UpdateValueCell,
        props: { disabled, serviceFieldOptions },
      },
      {
        header: "Sort",
        headerWidth: "10%",
        fieldName: "sortOrder",
        component: NumberInput,
        props: { size: "xs", min: 0 },
      },
    ],
    [disabled, serviceFieldOptions]
  );

  return (
    <Stack spacing="md">
      <Box
        sx={(theme) => ({
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[3]
          }`,
          borderRadius: theme.radius.sm,
          padding: theme.spacing.md,
          overflowX: "auto",
        })}
      >
        <ArrayTable
          form={form}
          name={name}
          fields={tableFields}
          rowInitValue={rowInitValue()}
          readonly={disabled}
          addButtonLabel="Add update"
          addButtonProps={{ variant: "light" }}
        />
      </Box>
      {updates.length === 0 ? (
        <Text size="xs" color="dimmed">
          No order updates configured. Use <strong>{i18n.t("Fixed value")}</strong> for
          automatic updates (e.g. Terminate → TER). Use{" "}
          <strong>{i18n.t("Service field value")}</strong> to apply what the user entered
          when creating the service.
        </Text>
      ) : null}
    </Stack>
  );
};

export default ServiceTypeCompleteUpdatesEditor;
